import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, User, Mail, Lock, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

const backendUrl = 'https://justurbane.in/backend_php/public';

const EmployeeEditorpage = () => {
  const { employeeId } = useParams(); // Get ID from URL
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    employee_name: '',
    email: '',
    emp_password: '',
    user_type: 'staff'
  });

  // Fetch data if in Edit Mode
  useEffect(() => {
    if (employeeId) {
      fetchEmployee();
    }
  }, [employeeId]);

  const fetchEmployee = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/admin/employee/show/${employeeId}`);
      const data = await response.json();
      setFormData({ ...data, emp_password: '' }); // Reset password field for security
    } catch (error) {
      toast.error("Failed to load employee data");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/admin/employee/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, employee_id: employeeId })
      });

      if (response.ok) {
        toast.success(employeeId ? 'Updated successfully!' : 'Created successfully!');
        navigate('/admin/employees'); // Adjust path as needed
      }
    } catch (error) {
      toast.error("Error saving data");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate(-1)} className="flex items-center space-x-2 text-gray-600">
              <ArrowLeft className="w-5 h-5" /> <span>Back</span>
            </button>
            <h1 className="text-xl font-bold">{employeeId ? 'Edit Employee' : 'Add New Employee'}</h1>
          </div>
          <button 
            onClick={handleSave} 
            disabled={saving}
            className="flex items-center space-x-2 bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Employee'}</span>
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                value={formData.employee_name}
                onChange={(e) => setFormData({...formData, employee_name: e.target.value})}
              />
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="email"
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password {employeeId && <span className="text-xs text-gray-400">(Leave blank to keep current)</span>}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="password"
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
                value={formData.emp_password}
                onChange={(e) => setFormData({...formData, emp_password: e.target.value})}
              />
            </div>
          </div>

          {/* User Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role / User Type</label>
            <div className="relative">
              <Shield className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <select
                className="w-full pl-10 pr-4 py-2 border rounded-lg appearance-none"
                value={formData.user_type}
                onChange={(e) => setFormData({...formData, user_type: e.target.value})}
              >
                <option value="staff">Staff</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeEditorpage;